import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { LayoutDashboard, UtensilsCrossed, ShoppingBag, Settings, BarChart3, LogOut, Bell, ExternalLink, Copy, Check, Plus, Trash2, Edit2, Save, X, ChevronDown, ChevronUp, ChevronRight, CreditCard, MessageSquare, Smartphone, Globe, Mail, MessageCircle, DollarSign, Lightbulb, TrendingUp, Pizza, Coffee, IceCream, Sandwich, Wine, Salad, Soup, Beef, ArrowLeft, Home, MoreVertical, GripVertical, SlidersHorizontal, Printer, ChefHat, Rocket } from 'lucide-react';

import NoFeeLogo, { NofeeNavLogo } from '../components/NoFeeLogo';
import PaymentMethodsPanel from '../components/PaymentMethodsPanel';
import { DELIVERY_RADIUS_OPTIONS, DEFAULT_DELIVERY_RADIUS } from '../utils/deliveryRadius';
import useZipLookup from '../hooks/useZipLookup';
import Insights from '../components/Insights';
import PremiumOverview from '../components/PremiumOverview';
import LanguageSwitcher from '../components/LanguageSwitcher';
import OnboardingBanner from '../components/OnboardingBanner';
import SetupScoreCard from '../components/SetupScoreCard';
import GrowMyRestaurantWelcome from '../components/GrowMyRestaurantWelcome';
import FastPerformancePanel from '../components/FastPerformancePanel';
import RestaurantStatusHero from '../components/RestaurantStatusHero';
import OrderChannelsForm from '../components/OrderChannelsForm';
import OrderFulfillmentForm from '../components/OrderFulfillmentForm';
import SalesTaxSettings from '../components/SalesTaxSettings';
import CuisineTypeSelector from '../components/CuisineTypeSelector';
import UpcomingHolidaysAlert from '../components/UpcomingHolidaysAlert';
import DashboardOrdersTab from '../components/dashboard/DashboardOrdersTab';
import DashboardNotificationsTab from '../components/dashboard/DashboardNotificationsTab';
import useDebouncedSave from '../hooks/useDebouncedSave';
import useOrderBell from '../hooks/useOrderBell';
import ImageCropperModal from '../components/ImageCropperModal';
import SaveStatusPill from '../components/SaveStatusPill';
import WelcomeFlowModal from '../components/WelcomeFlowModal';
import SubscriptionPanel from '../components/SubscriptionPanel';
import GoogleBusinessProfileConnect from '../components/GoogleBusinessProfileConnect';
import MoneyInput from '../components/MoneyInput';
import CoverPhotoPreview from '../components/CoverPhotoPreview';
import RestaurantStatusControl from '../components/RestaurantStatusControl';
import { notifyReadinessChanged } from '../utils/readinessBus';
import { US_STATES } from '../utils/usStates';
import { useTranslation } from 'react-i18next';

// Map common category names to lucide icons (case-insensitive). Falls back to UtensilsCrossed.
const getCategoryIcon = (name = '') => {
  const n = name.toLowerCase();
  if (/pizza/.test(n)) return Pizza;
  if (/(drink|bever|coffee|tea|juice|smoothie)/.test(n)) return Coffee;
  if (/(wine|bar|cocktail|beer|alcohol)/.test(n)) return Wine;
  if (/(dessert|ice|cake|sweet)/.test(n)) return IceCream;
  if (/(sandwich|burger|wrap)/.test(n)) return Sandwich;
  if (/(salad|veg)/.test(n)) return Salad;
  if (/(soup|stew|broth)/.test(n)) return Soup;
  if (/(steak|meat|beef|grill|bbq)/.test(n)) return Beef;
  return UtensilsCrossed;
};

/**
 * Dashboard — restaurant owner control panel.
 *
 * iter142 (Jul 2026) — Added `embedded` prop so the onboarding wizard
 * can render the EXACT SAME Menu Manager inside a modal without
 * navigating the owner away to `/dashboard`. When `embedded={true}`:
 *   • Force `tab` = 'menu' and disable tab switching
 *   • Hide the sticky top header, the left sidebar, the right hero
 *     aside, the welcome flow modal, the notification bell prompt,
 *     the OnboardingBanner / SetupScoreCard / RestaurantStatusHero /
 *     UpcomingHolidaysAlert / Gifts CTA / GrowMyRestaurantWelcome
 *     — everything that isn't strictly part of the Menu Manager.
 *   • Show a "Save & Return to Onboarding" header + close button
 *     that calls `onEmbeddedClose` so the wizard can mark the menu
 *     section complete and refresh readiness.
 *
 * All data (categories, items, uploads) still flows through the same
 * existing endpoints and the same Mongo collections — zero duplication,
 * zero new backend, zero new component. One Menu Manager, one code
 * path, one source of truth.
 */
const Dashboard = ({ embedded = false, onEmbeddedClose }) => {
  const { user, restaurant, token, logout, api, fetchMe } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Initial tab can be deep-linked via `?tab=menu` (etc.) so the
  // onboarding "Open Menu Manager →" CTA lands the owner directly on
  // the right tab instead of dropping them on Overview.
  const initialTab = (() => {
    if (embedded) return 'menu'; // iter142 — embedded mode is locked to menu.
    if (typeof window === 'undefined') return 'overview';
    const tabParam = new URLSearchParams(window.location.search).get('tab');
    const allowed = ['overview', 'orders', 'menu', 'analytics', 'settings', 'notifications', 'insights'];
    return allowed.includes(tabParam) ? tabParam : 'overview';
  })();
  const [tab, setTab] = useState(initialTab);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [tipVisible, setTipVisible] = useState(true);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  // ── Welcome flow modal (3-step intro shown ONCE post-signup) ─────────
  // Trigger is gated on the localStorage flag set inside the modal so
  // returning owners never see it again. We default to false and flip on
  // in the effect below once `restaurant.id` resolves.
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  useEffect(() => {
    if (embedded) return; // iter142 — never show welcome flow inside onboarding modal.
    if (!restaurant?.id) return;
    try {
      const seen = localStorage.getItem(WelcomeFlowModal.STORAGE_KEY(restaurant.id));
      if (!seen) setWelcomeOpen(true);
    } catch (e) { /* private mode / quota — fail silently */ }
  }, [restaurant?.id, embedded]);

  // Restaurant settings form
  const [settingsForm, setSettingsForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  // ZIP-based City/State autofill + mismatch validation (Feb 2026).
  const zipLookup = useZipLookup(token);

  // Category form
  const [newCat, setNewCat] = useState('');
  const [editingCat, setEditingCat] = useState(null); // {id, name, image_url}
  const [catForm, setCatForm] = useState({ name: '', image_url: '' });

  // Item form
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({ category_id: '', name: '', description: '', price: '', image_url: '', is_available: true, addons: [] });

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    // Option B onboarding flow (Feb 2026): Owners can access the dashboard
    // BEFORE completing payment. The subscription is the LAST step of the
    // funnel, not the first. A persistent banner (OnboardingBanner + a
    // dedicated trial pill below) guides them to activate their subscription
    // to publish the restaurant — but they're free to explore, build their
    // menu, and configure settings in the meantime. This dramatically
    // reduces signup-to-setup drop-off caused by an immediate paywall.
    loadData();
  }, [token, restaurant?.id, restaurant?.subscription_status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (restaurant) {
      setSettingsForm({
        name: restaurant.name || '',
        // ─── Cuisine types (new multi-select array) ─────────────────
        // Hydrate from the array if present; fall back to the legacy
        // single-string field. The mirror in the backend keeps the two
        // in sync on save.
        cuisine_types: (
          Array.isArray(restaurant.cuisine_types) && restaurant.cuisine_types.length
            ? restaurant.cuisine_types
            : (restaurant.cuisine_type
                ? String(restaurant.cuisine_type).split(',').map((s) => s.trim()).filter(Boolean)
                : [])
        ),
        address: restaurant.address || '',
        city: restaurant.city || '',
        state: restaurant.state || '',
        zip_code: restaurant.zip_code || '',
        phone: restaurant.phone || '',
        description: restaurant.description || '',
        logo_url: restaurant.logo_url || '',
        cover_url: restaurant.cover_url || '',
        delivery_fee: restaurant.delivery_fee || 0,
        min_order: restaurant.min_order || 0,
        delivery_radius_miles: restaurant.delivery_radius_miles ?? DEFAULT_DELIVERY_RADIUS,
        tax_rate_override_pct: restaurant.tax_rate_override_pct ?? null,
        // estimated_time intentionally NOT loaded into the form — legacy
        // field replaced by pickup_avg_minutes / delivery_avg_minutes via
        // the Order Fulfillment Settings card below.
        // Track new fulfillment toggles so we can conditionally hide
        // Delivery Fee when delivery is disabled. The OrderFulfillmentForm
        // owns the actual save; this is purely for label rendering.
        delivery_enabled: restaurant.delivery_enabled ?? restaurant.accepts_delivery ?? true,
        accepts_delivery: restaurant.accepts_delivery !== false,
        accepts_pickup: restaurant.accepts_pickup !== false,
        default_language: restaurant.default_language || '',
        manual_status: restaurant.manual_status === 'busy' ? 'busy' : 'open',
        manual_status_until: restaurant.manual_status_until || null,
        business_hours: restaurant.business_hours || {
          mon: {open: '11:00', close: '22:00'},
          tue: {open: '11:00', close: '22:00'},
          wed: {open: '11:00', close: '22:00'},
          thu: {open: '11:00', close: '22:00'},
          fri: {open: '11:00', close: '23:00'},
          sat: {open: '11:00', close: '23:00'},
          sun: {open: '12:00', close: '21:00'},
        },
      });
    }
  }, [restaurant]);

  const loadData = async () => {
    try {
      if (!restaurant) return;
      const [catRes, orderRes, analyticsRes, notifRes] = await Promise.all([
        api.get(`/restaurants/${restaurant.id}/categories`),
        api.get(`/restaurants/${restaurant.id}/orders`),
        api.get(`/restaurants/${restaurant.id}/analytics`),
        api.get(`/restaurants/${restaurant.id}/notifications`),
      ]);
      let cats = catRes.data;
      // iter147 — Removed auto-seed of demo menu (Pizza / Margherita / Pepperoni / etc).
      // Owner spec: EVERY new restaurant must start with a truly empty menu — the
      // seed was leaking fake dishes into the onboarding "Build Your Menu" step,
      // which was confusing for owners and looked unprofessional. The /seed-demo
      // endpoint on the backend is preserved (still callable manually for QA /
      // integration tests) but the automatic call on first Dashboard load is
      // removed. If `cats.length === 0` we simply render the existing empty
      // state ("No categories yet. Add your first category above!").
      setCategories(cats);
      setOrders(orderRes.data);
      setAnalytics(analyticsRes.data);
      setNotifications(notifRes.data);

      const restRes = await api.get(`/restaurants/${restaurant.id}`);
      setItems(restRes.data.items || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  const menuUrl = `${window.location.origin}/menu/${restaurant?.slug}`;
  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(menuUrl);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked (permission / insecure context) — silent */
    }
  };

  // Category actions
  const addCategory = async () => {
    if (!newCat.trim()) return;
    await api.post(`/restaurants/${restaurant.id}/categories`, { name: newCat, order: categories.length });
    setNewCat('');
    loadData();
  };

  const saveCategory = async () => {
    if (!editingCat || !catForm.name.trim()) return;
    await api.put(`/categories/${editingCat.id}`, { name: catForm.name, image_url: catForm.image_url });
    setEditingCat(null);
    setCatForm({ name: '', image_url: '' });
    loadData();
  };

  // ── Cropper modal state ────────────────────────────────────────────
  // After any food / cover / logo / category photo is picked, we route
  // through `ImageCropperModal` so the owner can drag + zoom before
  // saving. Auto-mode default (centered crop) covers 95% of uploads —
  // manual adjustment is a single drag away. The `cropperTarget` enum
  // tells the Save handler which form field to write back to.
  const [cropperSrc, setCropperSrc] = useState(null);
  const [cropperTarget, setCropperTarget] = useState(null); // 'item' | 'cover' | 'logo' | 'category'
  const CROP_ASPECT = { item: 4 / 3, cover: 16 / 9, logo: 1, category: 4 / 3 };
  const CROP_TITLE = {
    item: 'Adjust your food photo',
    cover: 'Adjust your restaurant cover photo',
    logo: 'Adjust your restaurant logo',
    category: 'Adjust your category photo',
  };

  const onCropperSave = (dataUri) => {
    if (cropperTarget === 'item') setItemForm((f) => ({ ...f, image_url: dataUri }));
    else if (cropperTarget === 'cover') setSettingsForm((f) => ({ ...f, cover_url: dataUri }));
    else if (cropperTarget === 'logo') setSettingsForm((f) => ({ ...f, logo_url: dataUri }));
    else if (cropperTarget === 'category') setCatForm((f) => ({ ...f, image_url: dataUri }));
    setCropperSrc(null);
    setCropperTarget(null);
  };
  const onCropperCancel = () => { setCropperSrc(null); setCropperTarget(null); };

  const onCatPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { alert('Use JPG, PNG or WebP'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
    try {
      const compressed = await compressImage(file);
      setCropperSrc(compressed);
      setCropperTarget('category');
    } catch {
      alert('Could not process image, please try another one.');
    }
    if (e.target) e.target.value = '';
  };

  // Item photo upload — owner uploads from device with auto-compression (JPG/PNG/WebP)
  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * (MAX / width)); width = MAX; }
          else { width = Math.round(width * (MAX / height)); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const mime = file.type === 'image/png' ? 'image/jpeg' : (file.type === 'image/webp' ? 'image/webp' : 'image/jpeg');
        resolve(canvas.toDataURL(mime, 0.85));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onItemPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { alert('Use JPG, PNG or WebP'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
    try {
      // Auto-mode default: pre-compress to a manageable data URI before
      // showing the cropper. The cropper itself will emit the final
      // cropped JPEG when the owner clicks Save.
      const compressed = await compressImage(file);
      setCropperSrc(compressed);
      setCropperTarget('item');
    } catch {
      alert('Could not process image, please try another one.');
    }
    // Reset the input so re-selecting the same file fires onChange again.
    if (e.target) e.target.value = '';
  };

  // Logo & Cover upload — replaces the legacy "paste URL" fields. We
  // compress client-side (the helper above) and let the autosave loop
  // PUT the data URL up to /api/restaurants/{id}, which migrates it to
  // Cloudinary on the way in. Owner sees only preview + buttons.
  const onLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { alert('Use JPG, PNG or WebP'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
    try {
      const compressed = await compressImage(file);
      setCropperSrc(compressed);
      setCropperTarget('logo');
    } catch {
      alert('Could not process image, please try another one.');
    }
    if (e.target) e.target.value = '';
  };
  const onCoverFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { alert('Use JPG, PNG or WebP'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
    try {
      const compressed = await compressImage(file);
      setCropperSrc(compressed);
      setCropperTarget('cover');
    } catch {
      alert('Could not process image, please try another one.');
    }
    if (e.target) e.target.value = '';
  };

  // Drag-and-drop reorder for items within a category
  const [dragItem, setDragItem] = useState(null); // {id, category_id}
  const reorderItems = async (catId, sourceId, targetId) => {
    const catItems = items.filter(i => i.category_id === catId);
    const sourceIdx = catItems.findIndex(i => i.id === sourceId);
    const targetIdx = catItems.findIndex(i => i.id === targetId);
    if (sourceIdx < 0 || targetIdx < 0 || sourceIdx === targetIdx) return;
    const reordered = [...catItems];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    // Optimistic local update
    const otherItems = items.filter(i => i.category_id !== catId);
    setItems([...otherItems, ...reordered]);
    // Persist new order to backend
    try {
      await Promise.all(reordered.map((it, idx) => api.put(`/items/${it.id}`, { order: idx })));
    } catch (e) {
      console.error('Reorder failed', e);
      loadData();
    }
  };

  const deleteCategory = async (id) => {
    await api.delete(`/categories/${id}`);
    loadData();
  };

  // Item actions
  const saveItem = async () => {
    // ─── Sanitize payload before sending ──────────────────────────
    // New flat add-ons UX: all add-ons live in a single auto-named
    // "Add-ons" group. The backend still expects modifier_groups for
    // backward compatibility, so we shape one optional group with
    // the add-ons the owner authored.
    const num = (v, fallback = 0) => {
      const n = parseFloat(v);
      return Number.isFinite(n) && n >= 0 ? n : fallback;
    };
    const intIn = (v, lo, hi, fallback) => {
      const n = parseInt(v, 10);
      if (!Number.isFinite(n)) return fallback;
      return Math.max(lo, Math.min(hi, n));
    };
    // Accept BOTH the new flat shape (`addons: []`) and any legacy
    // grouped shape — sanitize whichever is present.
    const flatAddons = Array.isArray(itemForm.addons) ? itemForm.addons : null;
    const legacyGroups = Array.isArray(itemForm.modifier_groups) ? itemForm.modifier_groups : [];

    // ─── Validate add-on names ────────────────────────────────────
    // Reject overly short names (< 3 chars) so the customer never sees
    // cryptic labels like "ba" or "cc" on the public menu. Empty rows
    // are silently dropped (already handled by the .filter below).
    const tooShort = (flatAddons || [])
      .map((a, idx) => ({
        idx,
        name: (a?.name || '').trim(),
      }))
      .filter((a) => a.name && a.name.length < 3);
    if (tooShort.length > 0) {
      const list = tooShort.map((a) => `"${a.name}"`).join(', ');
      toast.error('Add-on names are too short', {
        description: `Please write the full name (min. 3 characters) for: ${list}. Example: "Bacon" instead of "ba", "Cream Cheese" instead of "cc".`,
      });
      return;
    }

    // Normalise to Title Case so the public menu always reads
    // "Extra Cheese" / "Bacon" — never "extra cheese" / "BACON".
    const titleCase = (s) =>
      s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

    let cleanGroups = [];
    if (flatAddons && flatAddons.length) {
      const cleanOptions = flatAddons
        .map((a) => ({
          name: titleCase((a.name || '').trim()),
          price_delta: num(a.price_delta, 0),
          max_quantity: intIn(a.max_quantity, 1, 10, 1),
        }))
        .filter((o) => o.name);
      if (cleanOptions.length) {
        cleanGroups = [{
          name: 'Add-ons',
          required: false,
          min_select: 0,
          max_select: 99,
          options: cleanOptions,
        }];
      }
    } else if (legacyGroups.length) {
      cleanGroups = legacyGroups
        .map((g) => ({
          name: (g.name || 'Add-ons').trim() || 'Add-ons',
          required: !!g.required,
          min_select: Math.max(0, parseInt(g.min_select, 10) || 0),
          max_select: Math.max(1, parseInt(g.max_select, 10) || 1),
          options: (g.options || [])
            .map((o) => ({
              name: titleCase((o.name || '').trim()),
              price_delta: num(o.price_delta, 0),
              max_quantity: intIn(o.max_quantity, 1, 10, 1),
            }))
            .filter((o) => o.name),
        }))
        .filter((g) => g.options.length > 0);
    }

    // Strip the FE-only `addons` field so it never hits the backend.
    const { addons: _stripped, ...rest } = itemForm;
    const data = {
      ...rest,
      price: num(itemForm.price, 0),
      modifier_groups: cleanGroups,
    };

    try {
      if (editingItem) {
        await api.put(`/items/${editingItem}`, data);
      } else {
        if (!restaurant?.id) {
          toast.error('Cannot save — restaurant context is still loading. Please retry.');
          return;
        }
        await api.post(`/restaurants/${restaurant.id}/items`, data);
      }
      const addonCount = cleanGroups[0]?.options?.length || 0;
      toast.success(
        editingItem ? 'Item updated successfully' : 'Item added successfully',
        addonCount > 0
          ? { description: `${addonCount} add-on${addonCount > 1 ? 's' : ''} saved` }
          : undefined,
      );
      setShowItemForm(false);
      setEditingItem(null);
      setItemForm({ category_id: '', name: '', description: '', price: '', image_url: '', is_available: true, addons: [] });
      loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1).join('') || ''}: ${d.msg}`).join('\n')
        : (detail || err?.message || 'Could not save item.');
      toast.error('Could not save item', { description: msg });
    }
  };

  const deleteItem = async (id) => {
    await api.delete(`/items/${id}`);
    loadData();
  };

  const editItem = (item) => {
    // Flatten legacy modifier_groups → flat addons[] for the new UI.
    const flatAddons = [];
    (item.modifier_groups || []).forEach((g) => {
      (g.options || []).forEach((opt) => {
        flatAddons.push({
          name: opt.name || '',
          price_delta: typeof opt.price_delta === 'number' ? opt.price_delta : '',
          max_quantity: typeof opt.max_quantity === 'number' && opt.max_quantity > 0 ? opt.max_quantity : 1,
        });
      });
    });
    setItemForm({
      category_id: item.category_id,
      name: item.name,
      description: item.description,
      price: item.price,
      image_url: item.image_url || '',
      is_available: item.is_available,
      addons: flatAddons,
    });
    setEditingItem(item.id);
    setShowItemForm(true);
  };

  // Order actions
  const updateOrderStatus = async (orderId, status) => {
    await api.patch(`/orders/${orderId}/status`, { status });
    loadData();
  };

  // Accept / Reject — explicit endpoints (separate from the legacy
  // status-progression PATCH) so the backend fires customer notifications
  // for these transitions and bell logic stays clean.
  const acceptOrder = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/accept`);
      toast.success('Order accepted. Customer notified.');
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept the order.');
    }
  };
  const rejectOrder = async (orderId) => {
    if (!window.confirm('Reject this order? The customer will be notified.')) return;
    try {
      await api.post(`/orders/${orderId}/reject`);
      toast.success('Order rejected. Customer notified.');
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reject the order.');
    }
  };

  // ── Auto-poll orders every 15s so the bell rings even when the owner
  // is sitting on a tab other than Orders. Pauses on Backgrounded tab
  // to save bandwidth; resumes on focus return.
  useEffect(() => {
    if (!restaurant?.id) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const r = await api.get(`/restaurants/${restaurant.id}/orders`);
        if (!cancelled) setOrders(r.data);
      } catch {
        /* silent — next tick retries */
      }
    };
    const interval = setInterval(tick, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.id]);

  // Bell + Enable Sound prompt — rings on new pending order, repeats
  // every 20s while at least one pending order remains.
  const { enabled: bellEnabled, needsPrompt: bellNeedsPrompt, enable: enableBell } = useOrderBell(orders);

  // ── iter90 (Feb 2026) — Operational notification bell badge.
  // Reads the real unread-count from /api/restaurant/alerts/unread-count
  // (auto-evaluated against Stripe / status / profile state). Refreshes
  // every 60s and on tab focus so the bell stays in sync without WS.
  const [alertsUnread, setAlertsUnread] = useState(0);
  const refreshAlertsCount = React.useCallback(async () => {
    try {
      const r = await api.get('/restaurant/alerts/unread-count');
      setAlertsUnread(r.data?.unread_count || 0);
    } catch {
      /* silent */
    }
  }, []);
  useEffect(() => {
    if (!restaurant?.id) return undefined;
    refreshAlertsCount();
    const t = setInterval(refreshAlertsCount, 60000);
    const onVis = () => { if (document.visibilityState === 'visible') refreshAlertsCount(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [restaurant?.id, refreshAlertsCount]);

  // Settings
  //
  // Numeric fields (delivery_fee, min_order, etc.) live in the form
  // state as STRINGS while the owner is editing — that way they can
  // fully clear or retype a value without React forcing "0" back.
  // Just before persistence we coerce them to numbers (empty/invalid
  // becomes 0, the backend's default).
  const NUMERIC_FIELDS = ['delivery_fee', 'min_order', 'tax_rate_override_pct'];
  const normalizeForSave = (form) => {
    const out = { ...form };
    NUMERIC_FIELDS.forEach((k) => {
      if (k in out) {
        const v = out[k];
        if (v === '' || v === null || v === undefined) {
          // Treat blank delivery_fee / min_order as 0; tax override
          // null is meaningful (revert to auto-detect) so we keep null.
          out[k] = k === 'tax_rate_override_pct' ? null : 0;
        } else {
          const n = parseFloat(v);
          out[k] = Number.isFinite(n) ? n : (k === 'tax_rate_override_pct' ? null : 0);
        }
      }
    });
    return out;
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put(`/restaurants/${restaurant.id}`, normalizeForSave(settingsForm));
      await fetchMe();
      notifyReadinessChanged();
    } catch (err) {
      // The /api/restaurants/{id} endpoint now rejects with a 400
      // when the address can't be geocoded — surface the server's
      // user-friendly message so the owner knows what to fix.
      const msg = err?.response?.data?.detail || 'Could not save settings.';
      toast.error(msg);
      throw err;
    } finally {
      setSavingSettings(false);
    }
  };

  // ── Auto-save settings (Notion/Linear-style) ─────────────────────────
  // Fires 700 ms after the owner stops editing any field in the Settings
  // tab. `enabled` gates the hook to ONLY save when we already have a
  // restaurant in scope AND the user has touched the form at least once
  // (the hook's own `skipNext=1` swallows the burst of state-sets that
  // happen right after the initial fetch hydrates `settingsForm`).
  const { status: settingsSaveStatus, error: settingsSaveError } = useDebouncedSave(
    settingsForm,
    async (v) => {
      if (!restaurant?.id) return;
      await api.put(`/restaurants/${restaurant.id}`, normalizeForSave(v));
      // Avoid awaiting fetchMe() here — it would block the "saved" pill
      // and cause a full app re-fetch on every keystroke pause. The
      // restaurant context already has the up-to-date value optimistically.
      //
      // We DO notify the readiness bus so that any component watching
      // `/api/onboarding/readiness` (SetupScoreCard, RestaurantStatusHero,
      // OnboardingHubPage) re-validates its checklist immediately —
      // otherwise stale "❌ Phone Number Required" badges linger after
      // the autosave actually wrote the phone to the DB.
      notifyReadinessChanged();
    },
    { delay: 700, enabled: !!restaurant?.id && Object.keys(settingsForm).length > 0 && !zipLookup.warning },
  );

  const tabs = [
    { id: 'overview', label: t('dashboard.overview'), icon: LayoutDashboard },
    { id: 'menu', label: t('dashboard.menu'), icon: UtensilsCrossed },
    { id: 'orders', label: t('dashboard.orders'), icon: ShoppingBag },
    { id: 'insights', label: t('dashboard.insights'), icon: BarChart3 },
    { id: 'notifications', label: t('dashboard.notifications'), icon: Bell },
    { id: 'payments', label: t('dashboard.payments'), icon: CreditCard },
    { id: 'settings', label: t('dashboard.settings'), icon: Settings },
  ];

  // Track which categories are expanded (default: first one)
  const [expandedCats, setExpandedCats] = useState({});
  const [openCatMenu, setOpenCatMenu] = useState(null);
  // iter146 — refs to each category's 3-dot button so the embedded-mode
  // portal dropdown can compute its position from getBoundingClientRect().
  const catMenuBtnRefs = useRef({});
  const [catMenuPos, setCatMenuPos] = useState(null); // { top, left, placement }
  useLayoutEffect(() => {
    if (!embedded || !openCatMenu) { setCatMenuPos(null); return; }
    const btn = catMenuBtnRefs.current[openCatMenu];
    if (!btn) return;
    const compute = () => {
      const r = btn.getBoundingClientRect();
      const menuH = 148; // ~ 3 items + separator + padding
      const menuW = 176; // w-44
      const spaceBelow = window.innerHeight - r.bottom;
      const placement = spaceBelow < menuH + 12 ? 'top' : 'bottom';
      const top = placement === 'bottom' ? r.bottom + 4 : r.top - menuH - 4;
      const left = Math.max(8, Math.min(window.innerWidth - menuW - 8, r.right - menuW));
      setCatMenuPos({ top, left, placement });
    };
    compute();
    // Recompute on scroll / resize while open
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [embedded, openCatMenu]);
  useEffect(() => {
    if (categories.length && Object.keys(expandedCats).length === 0) {
      setExpandedCats({ [categories[0].id]: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    pending_restaurant_confirmation: 'bg-amber-100 text-amber-700',
    accepted_by_restaurant: 'bg-emerald-100 text-emerald-700',
    rejected_by_restaurant: 'bg-rose-100 text-rose-700',
    confirmed: 'bg-blue-100 text-blue-700',
    preparing: 'bg-orange-100 text-orange-700',
    ready: 'bg-purple-100 text-purple-700',
    out_for_delivery: 'bg-indigo-100 text-indigo-700',
    delivered: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  // Friendlier labels — restaurant operators see "Pending Confirmation"
  // instead of the snake_case status key.
  const statusLabels = {
    pending: 'Pending Confirmation',
    pending_restaurant_confirmation: 'Pending Confirmation',
    accepted_by_restaurant: 'Accepted',
    rejected_by_restaurant: 'Rejected',
    confirmed: 'Confirmed',
    preparing: 'Preparing',
    ready: 'Ready',
    out_for_delivery: 'Out for Delivery',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="animate-spin w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full"/></div>;

  return (
    <div className="min-h-screen bg-zinc-950 relative">
      {/* Welcome flow — auto-opens on first visit per restaurant. */}
      <WelcomeFlowModal
        open={welcomeOpen}
        restaurantId={restaurant?.id}
        onClose={() => setWelcomeOpen(false)}
        onComplete={() => {
          setWelcomeOpen(false);
          // Route them straight into the setup wizard so the next step
          // is obvious. If they're already published, the wizard simply
          // shows a success state.
          navigate('/onboarding/setup');
        }}
      />
      {/* Subtle premium glow effects */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-gold-500/[0.06] blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-amber-600/[0.04] blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-orange-500/[0.03] blur-3xl" />
      </div>
      <div className="relative z-10">
      {/* iter142 — Embedded mode top bar (used by onboarding modal). */}
      {embedded && (
        <header
          data-testid="dashboard-embedded-header"
          className="bg-zinc-900 border-b border-yellow-500/40 px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-yellow-500/15 border border-yellow-500/45 flex items-center justify-center flex-shrink-0">
              <UtensilsCrossed className="w-4 h-4 text-yellow-300" />
            </div>
            <div className="min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                Onboarding · Build Your Menu
              </p>
              <p className="text-sm font-semibold text-white truncate">
                Menu Manager — everything you build here is saved automatically.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEmbeddedClose?.()}
            data-testid="dashboard-embedded-close"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[13px] tracking-tight transition-all shadow-[0_10px_24px_-10px_rgba(250,204,21,0.55)]"
          >
            <Check className="w-4 h-4" />
            Save & Return to Onboarding
          </button>
        </header>
      )}
      {/* Top bar — regular Dashboard nav. Hidden in embedded mode. */}
      {!embedded && (
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <a href="/" className="hover:opacity-80 transition-opacity flex-shrink-0" title="Back to website" data-testid="dashboard-logo-home">
            <NofeeNavLogo height={32} variant="light" />
          </a>
          <span className="text-zinc-700 mx-1 hidden sm:inline">|</span>
          {/* Restaurant selector */}
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-zinc-800/70 transition-colors min-w-0"
            data-testid="dashboard-restaurant-selector"
            title={restaurant?.name}
          >
            <span className="text-zinc-200 font-semibold truncate max-w-[140px] sm:max-w-[220px]">{restaurant?.name}</span>
            <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          </button>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <a href={menuUrl} target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 bg-gold-500/15 text-gold-300 hover:bg-gold-500/25 rounded-lg text-sm font-semibold transition-colors border border-gold-500/40">
            <ExternalLink className="w-4 h-4" /> {t('dashboard.view_menu')}
          </a>
          <button onClick={copyLink} className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-sm text-zinc-200 transition-colors">
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            {copied ? t('dashboard.copied') : t('dashboard.menu_link')}
          </button>
          {/* Notification bell */}
          <button
            type="button"
            onClick={() => setTab('notifications')}
            className="relative p-2 rounded-full hover:bg-zinc-800 transition-colors"
            data-testid="dashboard-bell"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5 text-zinc-300" />
            {alertsUnread > 0 && (
              <span
                data-testid="dashboard-bell-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold flex items-center justify-center ring-2 ring-zinc-900"
              >
                {alertsUnread > 99 ? '99+' : alertsUnread}
              </span>
            )}
          </button>
          {/* User avatar dropdown */}
          <div className="relative" data-testid="dashboard-user-menu">
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-zinc-800/80 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold-400 to-amber-700 flex items-center justify-center text-black font-bold text-sm overflow-hidden ring-2 ring-gold-500/40">
                {restaurant?.logo_url ? (
                  <img src={restaurant.logo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span>{(user?.name || 'O').charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="hidden sm:block text-left leading-tight">
                <div className="text-xs font-bold text-white truncate max-w-[110px]">{user?.name || 'Owner'}</div>
                <div className="text-[10px] text-zinc-500">Owner</div>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-zinc-500 hidden sm:block" />
            </button>
            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-2 z-50 w-56 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl py-1.5">
                  <div className="px-4 py-2 border-b border-zinc-800">
                    <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                  </div>
                  <button onClick={() => { setUserMenuOpen(false); setTab('settings'); }} className="w-full text-left px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-800 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-gold-400" /> Settings
                  </button>
                  <a href="/" className="w-full text-left px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-800 flex items-center gap-2">
                    <Home className="w-4 h-4 text-gold-400" /> Back to website
                  </a>
                  <div className="my-1 border-t border-zinc-800" />
                  <div className="px-4 py-1.5"><LanguageSwitcher variant="dark" compact /></div>
                  <div className="my-1 border-t border-zinc-800" />
                  <button onClick={() => { logout(); navigate('/'); }} className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2">
                    <LogOut className="w-4 h-4" /> {t('common.logout')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
      )}

      <div className="flex">
        {/* Sidebar — hidden in embedded mode. */}
        {!embedded && (
        <aside className="w-56 bg-zinc-900 border-r border-zinc-800 min-h-[calc(100vh-57px)] sticky top-[57px] flex flex-col">
          <nav className="space-y-1 p-4 flex-1">
            {tabs.map(tabItem => (
              <button key={tabItem.id} onClick={() => setTab(tabItem.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  tab === tabItem.id ? 'bg-gold-500/15 text-gold-400 border border-gold-500/30' : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200'
                }`}>
                <tabItem.icon className="w-5 h-5" /> {tabItem.label}
                {tabItem.id === 'orders' && orders.filter(o => ['pending', 'pending_restaurant_confirmation'].includes(o.status)).length > 0 && (
                  <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {orders.filter(o => ['pending', 'pending_restaurant_confirmation'].includes(o.status)).length}
                  </span>
                )}
              </button>
            ))}

            {/* 🚀 Grow My Restaurant — proper nav item, navigates to /dashboard/grow */}
            <button
              onClick={() => navigate('/dashboard/grow')}
              data-testid="sidebar-grow-restaurant-btn"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200 transition-colors"
            >
              <Rocket className="w-5 h-5" />
              <span className="flex-1 text-left">Grow My Restaurant</span>
            </button>
          </nav>

          {/* Sidebar bottom cards */}
          <div className="p-4 space-y-3 border-t border-zinc-800">
            <div className="rounded-xl bg-gradient-to-br from-gold-500/15 to-zinc-800/20 border border-gold-500/30 p-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-gold-400" />
                <p className="text-xs font-bold text-white">Grow Your Business</p>
              </div>
              <p className="text-[11px] text-zinc-400 leading-snug mb-2">More visibility, more customers, more sales.</p>
              <button onClick={() => setTab('insights')} className="w-full py-2 bg-gold-500 hover:bg-gold-400 text-black text-xs font-bold rounded-lg transition-colors" data-testid="sidebar-upgrade-btn">
                Upgrade Plan
              </button>
            </div>
            <div className="rounded-xl bg-zinc-800/50 border border-zinc-700/50 p-3">
              <p className="text-xs font-bold text-white mb-1">Need Help?</p>
              <p className="text-[11px] text-zinc-400 mb-2">We&apos;re here for you.</p>
              <a href="mailto:support@nofee.restaurant" className="text-xs text-gold-400 font-semibold hover:text-gold-300 inline-flex items-center gap-1" data-testid="sidebar-support-link">
                Contact Support →
              </a>
            </div>
          </div>
        </aside>
        )}

        {/* Main */}
        <main className="flex-1 p-8 min-w-0">
          {/* iter91 — Global "Enable Sound" banner. Persists on EVERY tab
              (not just Orders) so the owner cannot forget to unlock the
              bell. Browsers block AudioContext until a user gesture —
              this banner IS the gesture. Disappears the moment audio is
              enabled. */}
          {bellNeedsPrompt && !embedded && (
            <button
              type="button"
              onClick={enableBell}
              data-testid="global-enable-sound-banner"
              className="w-full mb-5 px-5 py-3.5 rounded-2xl border border-amber-500/50 bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-amber-500/15 text-amber-100 text-[13.5px] font-semibold flex items-center justify-between gap-3 hover:from-amber-500/25 hover:to-amber-500/25 transition-all"
            >
              <span className="flex items-center gap-2.5">
                <Bell className="w-4 h-4 text-amber-300 animate-pulse" />
                Click to enable the order sound alert — without this your browser blocks the bell when new orders arrive.
              </span>
              <span className="px-3 py-1 rounded-lg bg-amber-400 text-black text-[12px] font-extrabold shrink-0">
                Enable Sound
              </span>
            </button>
          )}
          {/* Onboarding / status / promo cards — shown ONLY on Overview
              so secondary tabs (Menu, Orders, Insights, Payments,
              Settings) open straight to their content without making
              the owner scroll past them. Keeps the Overview tab as the
              single "what should I do next?" surface. */}
          {tab === 'overview' && (
            <>
              <OnboardingBanner />
              <SetupScoreCard />

              {/* Always-visible Restaurant Status Hero — the single source of
                  truth for what the owner should do next.  Replaces the old
                  eager "QR + Share" banner with a guided, status-aware panel.
                  QR / Share / Gifts unlock only after the restaurant is live. */}
              <RestaurantStatusHero token={token} restaurant={restaurant} />

              {/* Upcoming Holiday Schedule banner — surfaces special dates due
                  within 7 days (FEATURE #9). Hidden when there's nothing pending. */}
              <UpcomingHolidaysAlert token={token} restaurantId={restaurant?.id} />

              {/* NoFee Gift Redemption — entry point (LIVE restaurants only).
                  Before going live, this would distract the owner from the
                  primary task of building their menu. */}
              {restaurant?.go_live_at && (
                <a
                  href="/dashboard/gifts"
                  data-testid="dashboard-gifts-link"
                  className="mb-6 flex items-center justify-between gap-4 p-4 rounded-2xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/[0.06] via-amber-500/[0.04] to-yellow-500/[0.04] hover:border-yellow-500/50 hover:from-yellow-500/[0.10] transition-all group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-300 to-gold-500 flex items-center justify-center shrink-0">
                      <span className="text-black font-extrabold text-[15px]">$</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10.5px] uppercase tracking-[0.18em] text-gold-300 font-bold">NoFee Gifts</p>
                      <p className="text-[13.5px] text-white font-semibold truncate">Accept NoFee gift credits — reimbursed promptly via Stripe</p>
                    </div>
                  </div>
                  <span className="text-[12px] font-bold text-yellow-300 group-hover:translate-x-1 transition-transform shrink-0">Open →</span>
                </a>
              )}

              {/* 🚀 Grow My Restaurant — reminder card AND celebration
                  popup. The popup fires once per restaurant (localStorage)
                  immediately after Go Live, so owners who skip the
                  onboarding success page still see the post-launch
                  invitation to grow their restaurant. */}
              <GrowMyRestaurantWelcome restaurant={restaurant} variant="both" />
            </>
          )}

          {/* OVERVIEW */}
          {tab === 'overview' && (
            <PremiumOverview
              analytics={analytics}
              orders={orders}
              restaurant={restaurant}
              setTab={setTab}
              copyLink={copyLink}
            />
          )}

          {/* MENU */}
          {tab === 'menu' && (
            <div data-testid="menu-tab">
              <div className="mb-2">
                <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Menu Management</h2>
                <p className="text-sm md:text-base text-zinc-400 mt-2">Manage your categories and menu items.</p>
              </div>

              {/* Add Category */}
              <div className="flex gap-2 mt-6 mb-6">
                <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category name..."
                  className="px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder:text-zinc-500 focus:border-gold-500 outline-none flex-1"
                  onKeyDown={e => e.key === 'Enter' && addCategory()}
                  data-testid="menu-new-cat-input" />
                <button onClick={addCategory} className="px-5 py-3 bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/40 text-gold-300 hover:text-gold-200 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-testid="menu-add-cat-btn">
                  <Plus className="w-4 h-4" /> Add Category
                </button>
              </div>

              {/* Category Edit Modal — replaces icon with owner's photo */}
              {editingCat && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl p-6 w-full max-w-md" data-testid="cat-edit-modal">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-lg font-bold text-slate-900">Edit category</h3>
                      <button onClick={() => setEditingCat(null)}><X className="w-5 h-5 text-slate-400" /></button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                        <input value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none"
                          placeholder="e.g. Pizza, Drinks…" data-testid="cat-edit-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Category photo</label>
                        <p className="text-xs text-slate-500 mb-2">This photo replaces the default icon in the circle. Use a close-up of your dish or food.</p>
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 rounded-full border-2 border-dashed border-gold-400 bg-gold-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {catForm.image_url ? (
                              <img src={catForm.image_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <UtensilsCrossed className="w-7 h-7 text-gold-500" />
                            )}
                          </div>
                          <div className="flex flex-col gap-2 flex-1">
                            <label className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 bg-gold-500 hover:bg-gold-400 text-black text-sm font-bold rounded-lg" data-testid="cat-edit-upload-btn">
                              Upload photo
                              <input type="file" accept="image/*" onChange={onCatPhotoFile} className="hidden" data-testid="cat-edit-upload-input" />
                            </label>
                            <input value={catForm.image_url} onChange={e => setCatForm({ ...catForm, image_url: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs" placeholder="…or paste an image URL" />
                            {catForm.image_url && (
                              <button type="button" onClick={() => setCatForm({ ...catForm, image_url: '' })}
                                className="text-xs text-red-500 hover:underline self-start">Remove photo</button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button onClick={() => setEditingCat(null)} className="flex-1 py-3 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50">Cancel</button>
                        <button onClick={saveCategory} className="flex-1 py-3 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl flex items-center justify-center gap-2" data-testid="cat-edit-save">
                          <Save className="w-4 h-4" /> Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Item Form Modal (unchanged) */}
              {showItemForm && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-6">
                      <button
                        onClick={() => setShowItemForm(false)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                        data-testid="item-form-back"
                      >
                        <ArrowLeft className="w-4 h-4" /> Back
                      </button>
                      <h3 className="text-lg font-bold">{editingItem ? 'Edit Item' : 'Add Menu Item'}</h3>
                      <button onClick={() => setShowItemForm(false)} aria-label="Close" data-testid="item-form-close">
                        <X className="w-5 h-5 text-slate-400 hover:text-slate-700" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                        <select value={itemForm.category_id} onChange={e => setItemForm({...itemForm, category_id: e.target.value})}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none">
                          <option value="">Select category</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                        <input value={itemForm.name} onChange={e => setItemForm({...itemForm, name: e.target.value})}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none" placeholder="Burger, Pizza..." />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                        <textarea value={itemForm.description} onChange={e => setItemForm({...itemForm, description: e.target.value})}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none resize-none" rows={2} placeholder="Delicious..." />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Price ($)</label>
                        <MoneyInput
                          testid="dashboard-item-price"
                          value={itemForm.price}
                          onChange={(raw) => setItemForm({...itemForm, price: raw})}
                          onCommit={(n) => setItemForm({...itemForm, price: n === '' ? '' : n})}
                          placeholder="9.99"
                          inputClassName="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-slate-900 mb-1">Dish photo</label>
                        <p className="text-xs text-slate-500 mb-2">Upload a clear close-up of the dish. JPG, PNG or WebP, up to 10MB — we auto-compress for fast loading.</p>
                        <div className="flex items-center gap-4">
                          <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-gold-400 bg-gold-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {itemForm.image_url ? (
                              <img src={itemForm.image_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <UtensilsCrossed className="w-8 h-8 text-gold-500" />
                            )}
                          </div>
                          <div className="flex flex-col gap-2 flex-1">
                            <label className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 bg-gold-500 hover:bg-gold-400 text-black text-sm font-bold rounded-lg shadow-md transition-colors w-fit" data-testid="item-upload-btn">
                              {itemForm.image_url ? 'Replace photo' : 'Upload photo'}
                              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onItemPhotoFile} className="hidden" data-testid="item-upload-input" />
                            </label>
                            {itemForm.image_url && (
                              <button type="button" onClick={() => setItemForm({ ...itemForm, image_url: '' })}
                                className="text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors self-start"
                                data-testid="item-photo-remove">
                                Remove photo
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <label className="block text-sm font-bold text-slate-900">Add-ons</label>
                            <p className="text-xs text-slate-500 mt-0.5">Extras the customer can add to this item (e.g. Extra Cheese, Bacon, Coke). All optional.</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setItemForm({
                              ...itemForm,
                              addons: [...(itemForm.addons || []), { name: '', price_delta: '', max_quantity: 1 }],
                            })}
                            className="px-3 py-1.5 bg-gold-50 text-gold-700 text-xs font-bold rounded-lg hover:bg-gold-100"
                            data-testid="add-addon-btn"
                          >
                            + Add add-on
                          </button>
                        </div>

                        {(itemForm.addons || []).length > 0 && (
                          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 px-1 mb-1 text-[10.5px] uppercase tracking-wide text-slate-500 font-bold">
                            <span>Name</span>
                            <span className="w-24 text-right pr-2">Price Each ($)</span>
                            <span className="w-32 text-center">Customer Can Add Up To</span>
                            <span className="w-6" />
                          </div>
                        )}

                        {(itemForm.addons || []).map((addon, idx) => (
                          <div
                            key={idx}
                            className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 items-start mb-1.5"
                            data-testid={`addon-row-${idx}`}
                          >
                            <input
                              type="text"
                              value={addon.name}
                              onChange={(e) => {
                                const next = [...itemForm.addons];
                                next[idx] = { ...addon, name: e.target.value };
                                setItemForm({ ...itemForm, addons: next });
                              }}
                              className={`px-3 py-2 rounded-lg border text-sm focus:ring-2 outline-none transition-colors ${
                                addon.name && addon.name.trim().length > 0 && addon.name.trim().length < 3
                                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20 bg-red-50/50'
                                  : 'border-slate-200 focus:border-gold-500 focus:ring-gold-500/20'
                              }`}
                              placeholder="e.g. Extra Cheese, Bacon, Pepperoni"
                              data-testid={`addon-name-${idx}`}
                              minLength={3}
                              title={addon.name && addon.name.trim().length > 0 && addon.name.trim().length < 3
                                ? 'Please write the full name (e.g. "Bacon" not "ba"). Customers see this text on the menu.'
                                : 'Customer-facing add-on name'}
                            />
                            <div className="relative w-24">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-semibold pointer-events-none">+$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={addon.price_delta === '' || addon.price_delta == null ? '' : addon.price_delta}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => {
                                  // Allow free typing. Accept digits and one dot.
                                  let raw = e.target.value.replace(/[^0-9.]/g, '');
                                  // Keep only the first dot.
                                  const firstDot = raw.indexOf('.');
                                  if (firstDot !== -1) {
                                    raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
                                  }
                                  const next = [...itemForm.addons];
                                  next[idx] = { ...addon, price_delta: raw };
                                  setItemForm({ ...itemForm, addons: next });
                                }}
                                onBlur={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') return;
                                  const n = parseFloat(raw);
                                  if (Number.isFinite(n) && n >= 0) {
                                    const next = [...itemForm.addons];
                                    next[idx] = { ...addon, price_delta: Number(n.toFixed(2)) };
                                    setItemForm({ ...itemForm, addons: next });
                                  }
                                }}
                                className="w-24 pl-8 pr-2 py-2 rounded-lg border border-slate-200 text-sm text-right font-semibold focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none"
                                placeholder="0.00"
                                data-testid={`addon-price-${idx}`}
                              />
                            </div>
                            <select
                              value={addon.max_quantity || 1}
                              onChange={(e) => {
                                const next = [...itemForm.addons];
                                next[idx] = { ...addon, max_quantity: parseInt(e.target.value, 10) || 1 };
                                setItemForm({ ...itemForm, addons: next });
                              }}
                              className="w-32 px-2 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none"
                              data-testid={`addon-maxqty-${idx}`}
                              title="Maximum quantity the customer can pick — system will charge price × selected quantity"
                            >
                              {Array.from({ length: 20 }).map((_, n) => (
                                <option key={n + 1} value={n + 1}>{n + 1}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => {
                                const next = itemForm.addons.filter((_, i) => i !== idx);
                                setItemForm({ ...itemForm, addons: next });
                              }}
                              className="w-6 h-6 inline-flex items-center justify-center text-red-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                              data-testid={`addon-remove-${idx}`}
                              aria-label="Remove add-on"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            {/* Live max-extended-price hint. Spans columns 2-3
                                so it sits right under the price + max-qty
                                fields the owner just edited. */}
                            {(() => {
                              const unit = Number(addon.price_delta || 0);
                              const cap = Number(addon.max_quantity || 1);
                              if (unit <= 0) return null;
                              const total = (unit * cap).toFixed(2);
                              return (
                                <p
                                  className="col-start-2 col-span-2 text-[11px] text-slate-500 text-center mt-0.5"
                                  data-testid={`addon-${idx}-max-total-hint`}
                                >
                                  If customer picks {cap}, they pay {' '}
                                  <span className="font-semibold text-slate-700">${total}</span>
                                </p>
                              );
                            })()}
                          </div>
                        ))}

                        {(itemForm.addons || []).length === 0 && (
                          <p className="text-[12.5px] text-slate-400 italic px-1">
                            No add-ons yet. Click <span className="font-bold text-gold-600">+ Add add-on</span> to add an extra customers can choose.
                          </p>
                        )}
                      </div>

                      <button onClick={saveItem} className="w-full py-3 bg-gold-500 hover:bg-gold-400 text-black font-semibold rounded-xl transition-colors flex items-center justify-center gap-2">
                        <Save className="w-4 h-4" /> {editingItem ? 'Update Item' : 'Add Item'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Categories list — dark expandable cards */}
              <div className="space-y-3 mb-4" data-testid="menu-categories-list">
                <div className="flex items-center gap-2 text-zinc-400 text-sm mb-3">
                  <SlidersHorizontal className="w-4 h-4" /> <span className="font-semibold">Menu</span>
                </div>

                {categories.length === 0 ? (
                  <div className="text-center py-16 bg-zinc-900 rounded-2xl border border-zinc-800">
                    <UtensilsCrossed className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500">No categories yet. Add your first category above!</p>
                  </div>
                ) : categories.map(cat => {
                  const catItems = items.filter(i => i.category_id === cat.id);
                  const expanded = !!expandedCats[cat.id];
                  const totalPrice = catItems.reduce((s, i) => s + (i.price || 0), 0);
                  const IconComp = getCategoryIcon(cat.name);
                  const menuOpen = openCatMenu === cat.id;
                  const toggleExpand = () => setExpandedCats(p => ({ ...p, [cat.id]: !p[cat.id] }));
                  return (
                    <div key={cat.id} className={`bg-zinc-900 rounded-2xl border border-zinc-800 ${embedded ? '' : 'overflow-hidden'}`} data-testid={`menu-cat-${cat.id}`}>
                      {/* Category header — iter146: in embedded mode the whole row is clickable
                          to expand/collapse. The 3-dot menu uses stopPropagation to avoid
                          triggering the row toggle when its own button is clicked. */}
                      <div
                        className={`w-full flex items-center gap-4 px-5 py-4 hover:bg-zinc-800/40 transition-colors ${embedded ? 'cursor-pointer select-none' : ''}`}
                        onClick={embedded ? toggleExpand : undefined}
                        role={embedded ? 'button' : undefined}
                        tabIndex={embedded ? 0 : undefined}
                        onKeyDown={embedded ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(); } } : undefined}
                        data-testid={embedded ? `menu-cat-row-${cat.id}` : undefined}
                      >
                        {/* Chevron LEFT (toggle) */}
                        <button
                          type="button"
                          onClick={(e) => { if (embedded) e.stopPropagation(); toggleExpand(); }}
                          className="text-zinc-500 hover:text-gold-400 transition-colors flex-shrink-0"
                          data-testid={`menu-cat-toggle-${cat.id}`}
                          aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                          {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                        </button>
                        {/* Circular icon */}
                        <div className="w-11 h-11 rounded-full border border-gold-500/40 bg-gold-500/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {cat.image_url ? (
                            <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                          ) : (
                            <IconComp className="w-5 h-5 text-gold-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-bold truncate">{cat.name}</div>
                          <div className="text-xs text-zinc-500 mt-0.5">{catItems.length} {catItems.length === 1 ? 'item' : 'items'}</div>
                        </div>
                        <div className="text-gold-400 font-bold text-lg">${totalPrice.toFixed(2)}</div>
                        {/* Three-dot menu — iter146: in embedded mode, dropdown is
                            rendered via a Portal (see below) so the parent card's
                            overflow doesn't clip it. stopPropagation prevents the
                            row-level toggle from firing when this button is clicked. */}
                        <div className="relative flex-shrink-0" onClick={embedded ? (e) => e.stopPropagation() : undefined}>
                          <button
                            type="button"
                            onClick={(e) => { if (embedded) e.stopPropagation(); setOpenCatMenu(menuOpen ? null : cat.id); }}
                            className="p-2 hover:bg-zinc-700/50 rounded-lg text-zinc-500 hover:text-zinc-200 transition-colors"
                            data-testid={`menu-cat-more-${cat.id}`}
                            aria-label="Category actions"
                            ref={(el) => { if (el) catMenuBtnRefs.current[cat.id] = el; }}
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>
                          {menuOpen && !embedded && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenCatMenu(null)} />
                              <div className="absolute right-0 top-full mt-1 z-20 w-44 bg-zinc-800 border border-zinc-700 rounded-xl shadow-xl py-1" data-testid={`menu-cat-menu-${cat.id}`}>
                                <button
                                  onClick={() => { setOpenCatMenu(null); setEditingCat(cat); setCatForm({ name: cat.name, image_url: cat.image_url || '' }); }}
                                  className="w-full text-left px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-700 flex items-center gap-2"
                                  data-testid={`menu-cat-edit-${cat.id}`}
                                >
                                  <Edit2 className="w-4 h-4 text-gold-400" /> Edit category
                                </button>
                                <button
                                  onClick={() => { setOpenCatMenu(null); setShowItemForm(true); setEditingItem(null); setItemForm({ category_id: cat.id, name: '', description: '', price: '', image_url: '', is_available: true, addons: [] }); }}
                                  className="w-full text-left px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-700 flex items-center gap-2"
                                >
                                  <Plus className="w-4 h-4 text-gold-400" /> Add item
                                </button>
                                <div className="my-1 border-t border-zinc-700" />
                                <button
                                  onClick={() => { setOpenCatMenu(null); if (window.confirm(`Delete category "${cat.name}"?`)) deleteCategory(cat.id); }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                                  data-testid={`menu-cat-delete-${cat.id}`}
                                >
                                  <Trash2 className="w-4 h-4" /> Delete category
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Items list (expanded) */}
                      {expanded && (
                        <div className="border-t border-zinc-800 px-5 py-4 space-y-3" data-testid={`menu-cat-items-${cat.id}`}>
                          {catItems.length === 0 ? (
                            <div className="text-sm text-zinc-500 py-4 text-center">No items in this category yet</div>
                          ) : catItems.map(item => (
                            <div
                              key={item.id}
                              draggable
                              onDragStart={(e) => { setDragItem({ id: item.id, category_id: cat.id }); e.dataTransfer.effectAllowed = 'move'; }}
                              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                              onDrop={(e) => { e.preventDefault(); if (dragItem && dragItem.category_id === cat.id && dragItem.id !== item.id) { reorderItems(cat.id, dragItem.id, item.id); } setDragItem(null); }}
                              onDragEnd={() => setDragItem(null)}
                              className={`flex items-center gap-3 p-3 rounded-xl bg-zinc-950/60 border border-zinc-800 hover:border-gold-500/40 transition-colors cursor-move ${dragItem?.id === item.id ? 'opacity-40' : ''}`}
                              data-testid={`menu-item-${item.id}`}
                            >
                              {/* Drag handle */}
                              <GripVertical className="w-4 h-4 text-zinc-600 flex-shrink-0 hidden sm:block" />
                              {/* Photo (square rounded) or placeholder */}
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0 ring-1 ring-gold-500/30" />
                              ) : (
                                <div className="w-14 h-14 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0 ring-1 ring-zinc-700">
                                  <UtensilsCrossed className="w-6 h-6 text-zinc-600" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-white truncate">{item.name}</div>
                                {item.description && <div className="text-sm text-zinc-500 truncate">{item.description}</div>}
                              </div>
                              <div className="text-gold-400 font-bold flex-shrink-0">${item.price.toFixed(2)}</div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => editItem(item)} className="p-2 hover:bg-gold-500/10 rounded-lg transition-colors" data-testid={`menu-item-edit-${item.id}`} aria-label="Edit item">
                                  <Edit2 className="w-4 h-4 text-gold-400" />
                                </button>
                                <button onClick={() => { if (window.confirm(`Delete "${item.name}"?`)) deleteItem(item.id); }} className="p-2 hover:bg-red-500/10 rounded-lg transition-colors" data-testid={`menu-item-delete-${item.id}`} aria-label="Delete item">
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* + Add Item dashed button */}
                          <button
                            type="button"
                            onClick={() => { setShowItemForm(true); setEditingItem(null); setItemForm({ category_id: cat.id, name: '', description: '', price: '', image_url: '', is_available: true, addons: [] }); }}
                            className="w-full py-3 rounded-xl border-2 border-dashed border-gold-500/40 text-gold-400 hover:bg-gold-500/10 hover:border-gold-500/60 transition-colors text-sm font-semibold flex items-center justify-center gap-2"
                            data-testid={`menu-cat-add-item-${cat.id}`}
                          >
                            <Plus className="w-4 h-4" /> Add Item
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* iter146 — Portal-based dropdown for embedded mode.
                  Rendered at document.body level so the card's overflow
                  never clips the menu. Flips above the button when
                  there isn't enough space below. */}
              {embedded && openCatMenu && catMenuPos && (() => {
                const cat = categories.find(c => c.id === openCatMenu);
                if (!cat) return null;
                return createPortal(
                  <>
                    <div
                      className="fixed inset-0 z-[9998]"
                      onClick={() => setOpenCatMenu(null)}
                      data-testid="menu-cat-menu-backdrop"
                    />
                    <div
                      className="fixed z-[9999] w-44 bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl py-1"
                      style={{ top: catMenuPos.top, left: catMenuPos.left }}
                      data-testid={`menu-cat-menu-${cat.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => { setOpenCatMenu(null); setEditingCat(cat); setCatForm({ name: cat.name, image_url: cat.image_url || '' }); }}
                        className="w-full text-left px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-700 flex items-center gap-2"
                        data-testid={`menu-cat-edit-${cat.id}`}
                      >
                        <Edit2 className="w-4 h-4 text-gold-400" /> Edit category
                      </button>
                      <button
                        onClick={() => { setOpenCatMenu(null); setShowItemForm(true); setEditingItem(null); setItemForm({ category_id: cat.id, name: '', description: '', price: '', image_url: '', is_available: true, addons: [] }); }}
                        className="w-full text-left px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-700 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4 text-gold-400" /> Add item
                      </button>
                      <div className="my-1 border-t border-zinc-700" />
                      <button
                        onClick={() => { setOpenCatMenu(null); if (window.confirm(`Delete category "${cat.name}"?`)) deleteCategory(cat.id); }}
                        className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                        data-testid={`menu-cat-delete-${cat.id}`}
                      >
                        <Trash2 className="w-4 h-4" /> Delete category
                      </button>
                    </div>
                  </>,
                  document.body,
                );
              })()}

              {/* Tip for you banner */}
              {categories.length > 0 && tipVisible && (
                <div className="bg-zinc-900/80 backdrop-blur rounded-2xl border border-gold-500/20 p-5 flex items-center gap-4 flex-wrap shadow-[0_8px_32px_-12px_rgba(212,175,55,0.15)]" data-testid="menu-tip-banner">
                  <div className="w-11 h-11 rounded-full bg-gold-500/15 border border-gold-500/30 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-5 h-5 text-gold-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white">Tip for you</div>
                    <div className="text-sm text-zinc-400">Restaurants on NoFee grow 2.3× faster. Keep your menu updated to attract more customers!</div>
                  </div>
                  <button
                    onClick={() => setTab('insights')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gold-500/40 text-gold-400 hover:bg-gold-500/10 text-sm font-semibold transition-colors"
                  >
                    <TrendingUp className="w-4 h-4" /> View Insights
                  </button>
                  <button
                    type="button"
                    onClick={() => setTipVisible(false)}
                    className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
                    aria-label="Dismiss tip"
                    data-testid="menu-tip-dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ORDERS */}
          {tab === 'orders' && (
            <DashboardOrdersTab
              orders={orders}
              statusColors={statusColors}
              statusLabels={statusLabels}
              updateOrderStatus={updateOrderStatus}
              acceptOrder={acceptOrder}
              rejectOrder={rejectOrder}
              bellEnabled={bellEnabled}
              bellNeedsPrompt={bellNeedsPrompt}
              enableBell={enableBell}
              api={api}
              onOrdersReload={loadData}
            />
          )}

          {/* INSIGHTS */}
          {tab === 'insights' && (
            <Insights api={api} restaurant={restaurant} />
          )}

          {/* NOTIFICATIONS */}
          {tab === 'notifications' && (
            <DashboardNotificationsTab token={token} onChange={refreshAlertsCount} />
          )}

          {/* PAYMENTS — Stripe-only provider management */}
          {tab === 'payments' && (
            <PaymentMethodsPanel restaurant={restaurant} />
          )}

          {/* SETTINGS */}
          {tab === 'settings' && (
            <div className="max-w-2xl">
              <div className="flex items-center justify-between gap-3 mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Restaurant Settings</h2>
                <SaveStatusPill status={settingsSaveStatus} error={settingsSaveError} />
              </div>

              {/* Subscription management — live status from Stripe, cancel
                  / resume actions. Lives at the top of the settings tab so
                  the owner can find it instantly. Self-contained: handles
                  loading / error / no-subscription / scheduled-for-cancel /
                  active states internally. */}
              <div className="mb-6" data-testid="settings-subscription-block">
                <SubscriptionPanel
                  token={token}
                  onChangePlan={() => navigate('/onboarding/plan')}
                />
              </div>

              {/* Google Business Profile — owner connects their Google
                  Maps listing and one-click imports name, address, phone,
                  hours and website. Self-contained card: loads its own
                  status, surfaces a "Not configured" amber notice when
                  the platform-level GOOGLE_CLIENT_ID is missing. */}
              <div className="mb-6" data-testid="settings-gbp-block">
                <GoogleBusinessProfileConnect
                  token={token}
                  onImported={async () => {
                    if (typeof fetchMe === 'function') await fetchMe();
                    if (typeof loadData === 'function') await loadData();
                  }}
                />
              </div>

              {/* ─── Payments ───────────────────────────────────────────
                  Manage Stripe connection (bank, payouts, taxes, business
                  info) directly inside Settings. Stripe is the exclusive
                  payment provider. All sensitive financial data stays
                  inside Stripe — NoFee only displays bank last-4 + payout
                  summary. */}
              <div className="mb-6" data-testid="settings-payments-block">
                <PaymentMethodsPanel restaurant={restaurant} />
              </div>

              <p className="text-sm text-slate-500 -mt-4 mb-4" data-testid="settings-autosave-hint">
                Changes save automatically as you type.
              </p>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
                {[
                  { key: 'name', label: 'Restaurant Name', type: 'text' },
                  { key: 'address', label: 'Street address', type: 'text', hint: 'Street and number — city, state and ZIP go below.' },
                  { key: 'phone', label: 'Phone', type: 'tel' },
                  { key: 'description', label: 'Description', type: 'textarea' },
                  // cuisine_types, logo_url, cover_url moved to dedicated
                  // upload + multi-select widgets below — no more raw URL
                  // text fields.
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</label>
                    {f.hint && (
                      <p className="text-xs text-slate-500 mb-1.5">{f.hint}</p>
                    )}
                    {f.type === 'textarea' ? (
                      <textarea value={settingsForm[f.key] ?? ''} onChange={e => setSettingsForm({...settingsForm, [f.key]: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none resize-none" rows={3} data-testid={`settings-input-${f.key}`} />
                    ) : (
                      <input
                        type={f.type}
                        value={settingsForm[f.key] ?? ''}
                        onChange={e => setSettingsForm({...settingsForm, [f.key]: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none"
                        data-testid={`settings-input-${f.key}`}
                      />
                    )}
                  </div>
                ))}

                {/* ─── ZIP / City / State — auto-fill + validation ───
                    The `state` field is the canonical source for
                    `tax_service.resolve_tax_rate()` (USPS 2-letter code).
                    `city` is auto-populated from the ZIP via
                    `useZipLookup` on blur and the server validator
                    blocks any save where ZIP and State disagree
                    (Feb 2026 — prevents the MA+34746 bug class). */}
                <div className="space-y-4" data-testid="settings-tax-location">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">ZIP code</label>
                      <p className="text-xs text-slate-500 mb-1.5">Type your ZIP — we&apos;ll auto-fill City and State.</p>
                      <input
                        data-testid="settings-input-zip"
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        value={settingsForm.zip_code ?? ''}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^0-9\-]/g, '').slice(0, 10);
                          setSettingsForm({ ...settingsForm, zip_code: cleaned });
                          if (zipLookup.warning) zipLookup.clearWarning();
                        }}
                        onBlur={async () => {
                          const r = await zipLookup.resolve(settingsForm.zip_code, settingsForm.state);
                          if (r.valid && !r.mismatch) {
                            setSettingsForm((s) => ({
                              ...s,
                              state: r.state || s.state,
                              city: (s.city && s.city.trim()) ? s.city : (r.city || ''),
                            }));
                          }
                        }}
                        placeholder="34746"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">City</label>
                      <p className="text-xs text-slate-500 mb-1.5">Auto-filled from ZIP (editable).</p>
                      <input
                        data-testid="settings-input-city"
                        type="text"
                        value={settingsForm.city ?? ''}
                        onChange={(e) => setSettingsForm({ ...settingsForm, city: e.target.value })}
                        placeholder="Kissimmee"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">State</label>
                    <p className="text-xs text-slate-500 mb-1.5">Required for automatic sales tax calculation.</p>
                    <div className="relative">
                      <select
                        data-testid="settings-input-state"
                        value={settingsForm.state ?? ''}
                        onChange={async (e) => {
                          const next = e.target.value;
                          setSettingsForm({ ...settingsForm, state: next });
                          if (settingsForm.zip_code) {
                            await zipLookup.resolve(settingsForm.zip_code, next);
                          }
                        }}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none appearance-none pr-10 cursor-pointer bg-white"
                      >
                        <option value="">— Select state —</option>
                        {US_STATES.map((s) => (
                          <option key={s.code} value={s.code}>{`${s.code} — ${s.name}`}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">▾</div>
                    </div>
                  </div>
                  {zipLookup.warning && (
                    <div
                      data-testid="settings-zip-warning"
                      className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700"
                      role="alert"
                    >
                      {zipLookup.warning}
                    </div>
                  )}
                </div>

                {/* ─── Delivery Fee + Minimum Order ────────────────
                    Use the same MoneyInput component as the Onboarding
                    Setup page so the UX is consistent: typed-direct
                    text field (no spinner arrows), auto-select on
                    focus so typing replaces the existing value, and
                    "0" is NOT sticky — owners can fully clear the
                    field on tablets / mobile and retype without the
                    browser slamming "0" back in. The raw string lives
                    in state during typing; we commit to a number on
                    blur (via onCommit) and `normalizeForSave` handles
                    the autosave coercion. */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-testid="settings-delivery-fee-label">
                    {settingsForm.delivery_enabled === false
                      ? 'Delivery Fee ($) — only used when delivery is enabled below'
                      : 'Delivery Fee ($)'}
                  </label>
                  <MoneyInput
                    testid="settings-delivery-fee"
                    value={settingsForm.delivery_fee}
                    onChange={(raw) => setSettingsForm({ ...settingsForm, delivery_fee: raw })}
                    onCommit={(n) => setSettingsForm({ ...settingsForm, delivery_fee: n === '' ? 0 : n })}
                    inputClassName="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none"
                    prefix=""
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-testid="settings-min-order-label">
                    Minimum Order ($)
                  </label>
                  <MoneyInput
                    testid="settings-min-order"
                    value={settingsForm.min_order}
                    onChange={(raw) => setSettingsForm({ ...settingsForm, min_order: raw })}
                    onCommit={(n) => setSettingsForm({ ...settingsForm, min_order: n === '' ? 0 : n })}
                    inputClassName="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none"
                    prefix=""
                    placeholder="0.00"
                  />
                </div>

                {/* ─── Delivery Radius — required for checkout eligibility ───
                    Max distance from the restaurant ZIP centroid to the
                    customer's typed delivery ZIP. The customer checkout
                    calls /delivery-eligibility before payment and blocks
                    the Pay button when the customer is outside this
                    radius. Default = 5 mi. Future Fast by NoFee will
                    introduce a separate tighter `fast_delivery_radius_miles`
                    (default 2 mi). */}
                <div data-testid="settings-delivery-radius-section">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Delivery Radius (Miles)</label>
                  <p className="text-xs text-slate-500 mb-1.5">Maximum distance from your restaurant that you are willing to deliver.</p>
                  <div className="relative max-w-xs">
                    <select
                      data-testid="settings-input-delivery-radius"
                      value={settingsForm.delivery_radius_miles ?? DEFAULT_DELIVERY_RADIUS}
                      onChange={(e) => setSettingsForm({ ...settingsForm, delivery_radius_miles: Number(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none appearance-none pr-10 cursor-pointer bg-white"
                    >
                      {DELIVERY_RADIUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">▾</div>
                  </div>
                </div>

                {/* ─── Cuisine Types (multi-select) ──────────────── */}
                <div data-testid="settings-cuisine-types-section">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Cuisine Types</label>
                  <p className="text-xs text-slate-500 mb-3">
                    Pick the types of food you serve. Customers use these in search & filters.
                  </p>
                  <CuisineTypeSelector
                    value={settingsForm.cuisine_types || []}
                    onChange={(next) => setSettingsForm({ ...settingsForm, cuisine_types: next })}
                    variant="light"
                  />
                </div>

                {/* ─── Restaurant Logo (upload only — no URL field) ─── */}
                <div data-testid="settings-logo-section">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Restaurant Logo</label>
                  <p className="text-xs text-slate-500 mb-2">Your business logo shown throughout the platform.</p>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {settingsForm.logo_url ? (
                        <img src={settingsForm.logo_url} alt="Restaurant logo" className="w-full h-full object-cover" data-testid="settings-logo-preview" />
                      ) : (
                        <span className="text-[10px] text-slate-400 text-center px-1 leading-tight">No logo uploaded yet</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 bg-gold-500 hover:bg-gold-400 text-black text-sm font-bold rounded-lg shadow-sm transition-colors w-fit" data-testid="settings-logo-upload-btn">
                        {settingsForm.logo_url ? 'Upload New Logo' : 'Upload Logo'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onLogoFileChange} className="hidden" data-testid="settings-logo-upload-input" />
                      </label>
                      {settingsForm.logo_url && (
                        <button
                          type="button"
                          onClick={() => setSettingsForm({ ...settingsForm, logo_url: '' })}
                          data-testid="settings-logo-remove"
                          className="text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors self-start"
                        >
                          Remove Logo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* ─── Main Restaurant Photo (upload only — no URL field).
                    Renamed from "Cover Image" so restaurant owners
                    immediately understand this is their main public photo
                    customers see first, not a logo or dish photo. */}
                <div data-testid="settings-cover-section">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Main Restaurant Photo</label>
                  <p className="text-xs text-slate-500 mb-2">This photo appears in search results and at the top of your restaurant page.</p>
                  <div className="flex items-start gap-4">
                    <div className="w-36 h-20 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {settingsForm.cover_url ? (
                        <img src={settingsForm.cover_url} alt="Main restaurant photo" className="w-full h-full object-cover" data-testid="settings-cover-preview" />
                      ) : (
                        <span className="text-[10px] text-slate-400 text-center px-2 leading-tight">No main photo uploaded yet</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 bg-gold-500 hover:bg-gold-400 text-black text-sm font-bold rounded-lg shadow-sm transition-colors w-fit" data-testid="settings-cover-upload-btn">
                        {settingsForm.cover_url ? 'Upload New Main Photo' : 'Upload Main Photo'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onCoverFileChange} className="hidden" data-testid="settings-cover-upload-input" />
                      </label>
                      {settingsForm.cover_url && (
                        <button
                          type="button"
                          onClick={() => setSettingsForm({ ...settingsForm, cover_url: '' })}
                          data-testid="settings-cover-remove"
                          className="text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors self-start"
                        >
                          Remove Main Photo
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Live preview — same look-and-feel as the public
                      /restaurants directory card so the owner can confirm
                      the photo crops and frames correctly. */}
                  <div className="mt-4">
                    <CoverPhotoPreview
                      coverUrl={settingsForm.cover_url}
                      name={settingsForm.name || restaurant?.name}
                      cuisine={(settingsForm.cuisine_types && settingsForm.cuisine_types[0]) || settingsForm.cuisine_type}
                      testid="settings-cover-preview-card"
                    />
                  </div>
                </div>

                {/* Payment methods accepted */}
                <div className="border-t pt-5" data-testid="payment-methods-setting">
                  <label className="block text-sm font-bold text-slate-900 mb-1">Accepted Payment Methods</label>
                  <p className="text-xs text-slate-500 mb-3">
                    Stripe is the exclusive payment provider for credit cards, Apple Pay and Google Pay.
                  </p>

                  {/* Stripe — required, always shown, never toggleable. */}
                  <div
                    className="flex items-start gap-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50/60 max-w-md mb-2"
                    data-testid="payment-stripe-row"
                  >
                    <span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-emerald-500 items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </span>
                    <span>
                      <span className="block font-semibold text-slate-900">
                        Stripe <span className="text-slate-500 font-normal">(Cards, Apple Pay, Google Pay)</span>
                      </span>
                      <span className="block text-[12px] text-emerald-700 font-semibold leading-relaxed mt-0.5">
                        Enabled by default · Required
                      </span>
                    </span>
                  </div>
                </div>

                {/* Default menu language */}
                <div className="border-t pt-5" data-testid="default-language-setting">
                  <label className="block text-sm font-bold text-slate-900 mb-1">Default menu language</label>
                  <p className="text-xs text-slate-500 mb-3">
                    Language used when a first-time customer opens your public menu and their browser doesn&apos;t match a supported language. Customers can always switch using the language selector.
                  </p>
                  <select
                    value={settingsForm.default_language || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, default_language: e.target.value })}
                    className="w-full md:w-72 px-4 py-3 rounded-xl border border-slate-200 focus:border-gold-500 outline-none bg-white"
                    data-testid="default-language-select"
                  >
                    <option value="">Auto-detect from customer&apos;s browser</option>
                    <option value="en">🇺🇸 English</option>
                    <option value="pt">🇧🇷 Português</option>
                    <option value="es">🇪🇸 Español</option>
                    <option value="fr">🇫🇷 Français</option>
                    <option value="zh">🇨🇳 中文</option>
                    <option value="ar">🇸🇦 العربية</option>
                  </select>
                </div>

                {/* Business hours editor */}
                <div className="border-t pt-5">
                  <label className="block text-sm font-bold text-slate-900 mb-1">Business hours</label>
                  <p className="text-xs text-slate-500 mb-3">Toggle <strong>Open</strong> or <strong>Closed</strong> for each day, then set your operating hours.</p>
                  <div className="space-y-2" data-testid="business-hours-editor">
                    {['mon','tue','wed','thu','fri','sat','sun'].map(day => {
                      const hours = (settingsForm.business_hours || {})[day] || { open: '11:00', close: '22:00', closed: false };
                      const isOpen = !hours.closed;
                      const update = (patch) => {
                        const bh = { ...(settingsForm.business_hours || {}) };
                        bh[day] = { ...hours, ...patch };
                        setSettingsForm({ ...settingsForm, business_hours: bh });
                      };
                      const labels = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
                      return (
                        <div key={day} className="flex items-center gap-3 flex-wrap" data-testid={`hours-row-${day}`}>
                          <span className="w-24 text-sm font-medium text-slate-700">{labels[day]}</span>
                          {/* Segmented Open/Closed toggle — clearer than a single
                              checkbox. Two buttons side by side; the active one
                              is highlighted. Clicking "Closed" hides the time
                              inputs and shows "Closed all day". */}
                          <div
                            role="radiogroup"
                            aria-label={`${labels[day]} availability`}
                            className="inline-flex p-0.5 rounded-lg border border-slate-200 bg-slate-50 shrink-0"
                          >
                            <button
                              type="button"
                              role="radio"
                              aria-checked={isOpen}
                              data-testid={`hours-${day}-open-btn`}
                              onClick={() => update({ closed: false })}
                              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                                isOpen
                                  ? 'bg-gold-500 text-black shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                            >
                              Open
                            </button>
                            <button
                              type="button"
                              role="radio"
                              aria-checked={!isOpen}
                              data-testid={`hours-${day}-closed-btn`}
                              onClick={() => update({ closed: true })}
                              className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${
                                !isOpen
                                  ? 'bg-slate-700 text-white shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                            >
                              Closed
                            </button>
                          </div>
                          {isOpen ? (
                            <>
                              <input type="time" value={hours.open} onChange={e => update({ open: e.target.value })}
                                className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid={`hours-${day}-open`} />
                              <span className="text-slate-400 text-sm">–</span>
                              <input type="time" value={hours.close} onChange={e => update({ close: e.target.value })}
                                className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid={`hours-${day}-close`} />
                            </>
                          ) : (
                            <span className="text-sm italic text-slate-400" data-testid={`hours-${day}-closed-label`}>
                              Closed all day
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Restaurant Status — manual override that lets the owner
                    pause new orders without changing business hours. Saves
                    immediately on toggle (independent of the bulk Save). */}
                <div className="border-t pt-5" data-testid="settings-restaurant-status-block">
                  <label className="block text-sm font-bold text-slate-900 mb-1">Restaurant Status</label>
                  <p className="text-xs text-slate-500 mb-3">
                    Temporarily pause new orders when the kitchen is overloaded. Your menu, photos and prices stay visible.
                  </p>
                  <RestaurantStatusControl
                    value={settingsForm.manual_status}
                    until={settingsForm.manual_status_until}
                    light
                    onChange={async (s, untilISO) => {
                      setSettingsForm((f) => ({ ...f, manual_status: s, manual_status_until: untilISO }));
                      if (!restaurant?.id) return;
                      try {
                        await axios.put(
                          `${API}/api/restaurants/${restaurant.id}`,
                          { manual_status: s, manual_status_until: untilISO },
                          { headers: { Authorization: `Bearer ${token}` } },
                        );
                      } catch (_e) {
                        // The debounced settings auto-save will recover on
                        // the next field edit; surface nothing here.
                      }
                    }}
                    testid="settings-restaurant-status"
                  />
                </div>
                {/* Manual fallback — auto-save handles the common case but
                    keeping an explicit button preserves the option for
                    users who want a confirmation click after editing
                    multiple fields at once. */}
                <button
                  onClick={saveSettings}
                  disabled={savingSettings || settingsSaveStatus === 'saving'}
                  data-testid="save-settings-btn"
                  className="w-full py-3 border border-slate-200 hover:border-gold-400 text-slate-700 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" /> {savingSettings ? 'Saving...' : 'Save now'}
                </button>
              </div>

              {/* Stripe Section */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6">
                <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2"><CreditCard className="w-5 h-5 text-gold-500" /> Stripe Integration</h3>
                <p className="text-sm text-slate-500 mb-4">Connect your Stripe account to receive payments directly.</p>
                <div className="p-4 bg-gold-50 rounded-xl border border-gold-200">
                  <p className="text-sm text-gold-700">Status: {restaurant?.stripe_account_id ? <span className="font-bold text-green-600">Connected</span> : <span className="font-bold text-orange-600">Not connected (Demo mode - payments simulated)</span>}</p>
                  {!restaurant?.stripe_account_id && <p className="text-xs text-gold-600 mt-1">Add your Stripe Secret Key in the backend .env to enable real payments.</p>}
                </div>
              </div>

              {/* SMS/WhatsApp Section — REPLACED by Order Receiving Channels */}
              <div className="bg-zinc-950 rounded-2xl border border-zinc-800 p-6 mt-6" data-testid="dashboard-order-channels-section">
                <OrderChannelsForm restaurantId={restaurant?.id} token={token} />
              </div>

              {/* Order Fulfillment Settings — Pickup & Delivery operating settings */}
              <div className="bg-zinc-950 rounded-2xl border border-zinc-800 p-6 mt-6" data-testid="dashboard-fulfillment-section">
                <OrderFulfillmentForm restaurantId={restaurant?.id} token={token} />
              </div>

              {/* Sales Tax — auto-detected from restaurant's state +
                  optional local override. Rendered as white card so it
                  visually pairs with checkout flow which is also light. */}
              <div className="mt-6" data-testid="dashboard-sales-tax-section">
                <SalesTaxSettings
                  restaurantId={restaurant?.id}
                  getAuthHeader={() => ({ Authorization: `Bearer ${token}` })}
                />
              </div>

              {/* Fast by NoFee — performance panel sits right below
                  Fulfillment Settings so the owner can see the impact
                  of their promised times on customer trust. */}
              <FastPerformancePanel restaurantId={restaurant?.id} token={token} />
            </div>
          )}
        </main>

        {/* RIGHT HERO COLUMN — hidden in embedded mode (iter142). */}
        {!embedded && (
        <aside className="hidden xl:block flex-shrink-0 w-[44vw] max-w-[860px] xl:w-[42vw] sticky top-[57px] h-[calc(100vh-57px)] overflow-hidden border-l border-zinc-800" data-testid="dashboard-hero-aside">
          <img
            src="/dashboard-hero.jpg"
            alt="Built for restaurants. Powered by fairness."
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          {/* Brand-consistency overlay — fully erases the off-brand logo
              baked into the shirt of the photograph and renders the
              OFFICIAL NoFee lockup (identical asset to the header).

              The MASK is sized to cover the entire original lockup (icon
              circle + wordmark) so no remnants ever show through. The
              LOGO inside is intentionally smaller than the mask for a
              natural "printed-on-shirt" look. */}
          <div
            className="pointer-events-none absolute z-[1]"
            style={{ top: '38%', left: '42%', width: '30%', height: '10%' }}
            aria-hidden="true"
            data-testid="dashboard-hero-brand-overlay"
          >
            {/* Solid opaque mask — sized to fully cover the original
                off-brand lockup (icon + wordmark) leaving zero remnants. */}
            <div
              className="absolute inset-0"
              style={{
                background: '#0a0a0a',
                borderRadius: '3px',
                boxShadow: '0 0 0 10px #0a0a0a',
              }}
            />
            {/* Authentic official lockup, intentionally small for a natural
                printed-on-shirt look. Same exact asset as the navbar. */}
            <div className="absolute inset-0 flex items-center justify-center">
              <NofeeNavLogo height={30} variant="light" />
            </div>
          </div>
          {/* Cinematic golden vignette overlay for warmth + depth */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-zinc-950/40 via-transparent to-zinc-950/10" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-zinc-950/60 via-transparent to-transparent" />
          <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_120px_30px_rgba(0,0,0,0.45)]" />
        </aside>
        )}
      </div>
      </div>

      {/* Global image cropper — opens whenever a food/cover/logo/category
          photo is picked. Portaled to body so navbars / dropdowns
          can't trap it inside their stacking contexts. */}
      <ImageCropperModal
        open={!!cropperSrc}
        src={cropperSrc}
        aspect={CROP_ASPECT[cropperTarget] || 4 / 3}
        title={CROP_TITLE[cropperTarget] || 'Adjust photo'}
        onSave={onCropperSave}
        onCancel={onCropperCancel}
      />
    </div>
  );
};

export default Dashboard;
